/**
 * Mobile collection filters GB experiment.
 * - Frenzy bridge (presentation → native Frenzy inputs)
 * - GrowthBook activation (feature: mobile-collection-filters-layout)
 * QA override (mobile): ?gb_filters=1
 */
(function initFrenzyCollectionFiltersGb(global) {
  const MOBILE_MAX_WIDTH = 1023;

  function handleize(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  }

  function isMobileViewport() {
    return window.innerWidth <= MOBILE_MAX_WIDTH;
  }

  class FrenzyCollectionFiltersGb {
    constructor() {
      this.active = false;
      this.gbActiveCategory = null;
      this.syncObserver = null;
      this.frenzyMountObserver = null;
      this.frenzyPage = null;
    }

    getGbShell() {
      return document.querySelector('[data-collection-filters-gb]');
    }

    setActive(isOn) {
      this.active = Boolean(isOn) && isMobileViewport() && Boolean(this.getFrenzyPage());

      const gbShell = this.getGbShell();
      if (gbShell) {
        gbShell.hidden = !this.active;
        gbShell.setAttribute('aria-hidden', this.active ? 'false' : 'true');
      }

      document.documentElement.classList.toggle('gb-mobile-collection-filters--frenzy', this.active);

      if (this.active) {
        this.mount();
      } else {
        this.unmount();
      }
    }

    getFrenzyPage() {
      return document.querySelector('.frenzy_collections_page');
    }

    getFilterVisualsJson() {
      const node = document.querySelector('#filter-visuals-json');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (_error) {
        return {};
      }
    }

    resolveFilterVisual(groupTitle, label) {
      const visuals = this.getFilterVisualsJson();
      const key = `${handleize(groupTitle)}:${handleize(label)}`;
      const visual = visuals[key];
      const img =
        visual?.image ||
        document
          .querySelector(
            `.frenzy_collections_page .filter_checkbox[data-value="${label}"] img, .frenzy_collections_page .filter_checkbox[data-value="${label}"] .swatchOption`
          )
          ?.getAttribute?.('style')
          ?.match(/url\(["']?([^"')]+)/)?.[1];

      return {
        image: visual?.image || img || null,
        label: visual?.label || label,
      };
    }

    waitForFrenzyFilters() {
      return new Promise((resolve) => {
        const done = () => {
          const sections = this.getFilterSections();
          if (sections.length > 0) {
            resolve(sections);
            return true;
          }
          return false;
        };

        if (done()) return;

        const page = this.getFrenzyPage();
        if (!page) {
          resolve([]);
          return;
        }

        const observer = new MutationObserver(() => {
          if (done()) {
            observer.disconnect();
          }
        });

        observer.observe(page, { childList: true, subtree: true });
        window.setTimeout(() => {
          observer.disconnect();
          resolve(this.getFilterSections());
        }, 15000);
      });
    }

    getFilterSections() {
      const page = this.getFrenzyPage();
      if (!page) return [];

      const sectionNodes = page.querySelectorAll(
        '.frenzy_flex_topbar_filter_col .filter_widget_section, .ffc_sidebar_col .filter_widget_section, .frenzy_topbar_filter .filter_widget_section'
      );

      const sections = [];

      sectionNodes.forEach((sectionNode) => {
        const trigger = sectionNode.querySelector('.filter_widget_trigger');
        const title = trigger?.textContent?.replace(/\s+/g, ' ').trim();
        if (!title) return;

        const items = [];
        sectionNode.querySelectorAll('.filter_checkbox').forEach((checkbox) => {
          const input = checkbox.querySelector('input.filter_input_value');
          if (!input) return;

          const label =
            checkbox.querySelector('.filter_checkbox_label')?.textContent?.replace(/\s+/g, ' ').trim() ||
            input.value;

          const swatchImg = checkbox.querySelector('img');
          items.push({
            title,
            label,
            input,
            checkbox,
            imageSrc: swatchImg?.src || swatchImg?.getAttribute('data-src') || null,
          });
        });

        if (items.length > 0) {
          sections.push({ title, items, sectionNode });
        }
      });

      const uniqueByTitle = new Map();
      sections.forEach((section) => {
        const key = handleize(section.title);
        if (!uniqueByTitle.has(key)) {
          uniqueByTitle.set(key, section);
        }
      });

      return Array.from(uniqueByTitle.values());
    }

    hideNativeUi() {
      const page = this.getFrenzyPage();
      if (!page) return;

      const hideTargets = page.querySelectorAll(
        '.frenzy_flex_topbar_btn_col, .frenzy_flex_topbar_filter_col'
      );

      hideTargets.forEach((el) => {
        el.classList.add('frenzy-filters-gb-sr');
        el.setAttribute('aria-hidden', 'true');
      });
    }

    showNativeUi() {
      const page = this.getFrenzyPage();
      if (!page) return;

      page.querySelectorAll('.frenzy-filters-gb-sr').forEach((el) => {
        el.classList.remove('frenzy-filters-gb-sr');
        el.removeAttribute('aria-hidden');
      });
    }

    mount() {
      this.frenzyPage = this.getFrenzyPage();
      const gbShell = this.getGbShell();
      if (!this.frenzyPage || !gbShell) return;

      this.watchFrenzyMount();

      this.waitForFrenzyFilters().then((sections) => {
        if (!this.active) return;
        this.hideNativeUi();
        if (sections.length > 0) {
          this.render(sections);
          this.startSyncObserver();
        }
      });
    }

    watchFrenzyMount() {
      const page = this.getFrenzyPage();
      if (!page || this.frenzyMountObserver) return;

      this.frenzyMountObserver = new MutationObserver(() => {
        if (!this.active || !this.getGbShell()) return;

        const sections = this.getFilterSections();
        if (sections.length > 0) {
          this.hideNativeUi();
          this.render(sections);
        }
      });

      this.frenzyMountObserver.observe(page, { childList: true, subtree: true });
    }

    unmount() {
      this.showNativeUi();
      if (this.syncObserver) {
        this.syncObserver.disconnect();
        this.syncObserver = null;
      }
      if (this.frenzyMountObserver) {
        this.frenzyMountObserver.disconnect();
        this.frenzyMountObserver = null;
      }
    }

    startSyncObserver() {
      const page = this.getFrenzyPage();
      if (!page || !(page instanceof Node)) return;

      if (this.syncObserver) {
        this.syncObserver.disconnect();
      }

      this.syncObserver = new MutationObserver(() => {
        if (!this.active) return;
        const sections = this.getFilterSections();
        if (sections.length) {
          this.renderValues(sections);
        }
      });

      this.syncObserver.observe(page, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'class'],
      });
    }

    render(sections) {
      this.renderCategories(sections);
      this.renderValues(sections);
    }

    renderCategories(sections) {
      const gbShell = this.getGbShell();
      if (!gbShell) return;

      const track = gbShell.querySelector('[data-gb-categories-track]');
      if (!track) return;

      const activeExists = sections.some((s) => handleize(s.title) === this.gbActiveCategory);
      if (!this.gbActiveCategory || !activeExists) {
        this.gbActiveCategory = handleize(sections[0].title);
      }

      track.innerHTML = '';

      for (const section of sections) {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'collection-filters-gb__category-pill';
        if (handleize(section.title) === this.gbActiveCategory) {
          pill.classList.add('is-active');
        }
        pill.textContent = section.title;
        pill.addEventListener('click', () => {
          this.gbActiveCategory = handleize(section.title);
          this.renderCategories(sections);
          this.renderValues(sections);
        });
        track.appendChild(pill);
      }

    }

    renderValues(sections) {
      const gbShell = this.getGbShell();
      if (!gbShell) return;

      const track = gbShell.querySelector('[data-gb-values-track]');
      if (!track) return;

      const activeSection =
        sections.find((section) => handleize(section.title) === this.gbActiveCategory) || sections[0];

      track.innerHTML = '';

      const useCheckbox = handleize(activeSection.title) === 'size';

      for (const item of activeSection.items) {
        const visual = this.resolveFilterVisual(activeSection.title, item.label);
        const imageSrc = visual.image || item.imageSrc;
        const isSelected = item.input.checked;

        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'collection-filters-gb__value-option';
        if (useCheckbox) {
          option.classList.add('collection-filters-gb__value-option--checkbox');
        }
        if (isSelected) {
          option.classList.add('is-selected');
        }

        const thumb = document.createElement('span');
        thumb.className = 'collection-filters-gb__value-thumb';

        if (useCheckbox) {
          thumb.classList.add('collection-filters-gb__value-thumb--checkbox');
        } else if (imageSrc) {
          const image = document.createElement('img');
          image.src = imageSrc;
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          thumb.appendChild(image);
        }

        const label = document.createElement('span');
        label.className = 'collection-filters-gb__value-label';
        label.textContent = visual.label;

        option.append(thumb, label);
        option.addEventListener('click', () => {
          item.input.click();
          window.setTimeout(() => this.render(sections), 50);
        });

        track.appendChild(option);
      }
    }

  }

  global.FrenzyCollectionFiltersGb = new FrenzyCollectionFiltersGb();

  if (typeof global.frenzyScriptReload === 'function') {
    const originalFrenzyScriptReload = global.frenzyScriptReload;
    global.frenzyScriptReload = function frenzyScriptReloadWithGb(...args) {
      const result = originalFrenzyScriptReload.apply(this, args);
      if (global.FrenzyCollectionFiltersGb?.active) {
        global.FrenzyCollectionFiltersGb.mount();
      }
      return result;
    };
  }
})(window);

(function initCollectionFiltersGbExperiment() {
  const FEATURE_KEY = 'mobile-collection-filters-layout';
  const MOBILE_MAX_WIDTH = 1023;

  function isMobileViewport() {
    return window.innerWidth <= MOBILE_MAX_WIDTH;
  }

  function isQaOverrideEnabled() {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('gb_filters');
    if (value === '1' || value === 'true') {
      return true;
    }
    try {
      return window.localStorage.getItem('gb_filters') === '1';
    } catch (_error) {
      return false;
    }
  }

  function getProductFilters() {
    return document.querySelector('product-filters[data-collection-handle]');
  }

  function hasFrenzyCollectionPage() {
    return Boolean(document.querySelector('.frenzy_collections_page'));
  }

  function getGrowthBookInstance(gbInstance) {
    return gbInstance || window.__collectionFiltersGbGrowthbook || window.growthbook || null;
  }

  function readFeatureFlag(gbInstance) {
    if (isQaOverrideEnabled()) {
      return true;
    }

    const gb = getGrowthBookInstance(gbInstance);
    if (!gb || typeof gb.isOn !== 'function') {
      return false;
    }

    return gb.isOn(FEATURE_KEY);
  }

  function applyExperimentState(isOn) {
    const root = document.documentElement;
    const productFilters = getProductFilters();
    const isActive = Boolean(isOn) && isMobileViewport();
    const useFrenzyBridge = hasFrenzyCollectionPage() && window.FrenzyCollectionFiltersGb;

    if (useFrenzyBridge) {
      root.classList.toggle('gb-mobile-collection-filters', isActive);
      window.FrenzyCollectionFiltersGb.setActive(isActive);

      if (productFilters) {
        productFilters.dataset.mobileUi = 'control';
      }

      return;
    }

    root.classList.remove('gb-mobile-collection-filters');

    const gbShell = document.querySelector('[data-collection-filters-gb]');

    if (gbShell) {
      gbShell.hidden = true;
      gbShell.setAttribute('aria-hidden', 'true');
    }

    if (productFilters) {
      productFilters.dataset.mobileUi = 'control';
    }
  }

  function sync(gbInstance) {
    applyExperimentState(readFeatureFlag(gbInstance));
  }

  function attachToGrowthBook(gb) {
    if (!gb) return;

    window.__collectionFiltersGbGrowthbook = gb;
    window.growthbook = gb;

    if (typeof gb.setRenderer === 'function') {
      gb.setRenderer(function collectionFiltersGbRenderer() {
        sync(gb);
      });
    }

    sync(gb);
  }

  window.__collectionFiltersGbSync = sync;

  if (window.__collectionFiltersGbGrowthbook) {
    attachToGrowthBook(window.__collectionFiltersGbGrowthbook);
  } else {
    window.growthbook_queue = window.growthbook_queue || [];
    window.growthbook_queue.push((gb) => {
      attachToGrowthBook(gb);
    });
  }

  document.addEventListener('growthbookdata', () => {
    sync(getGrowthBookInstance());
  });

  document.addEventListener('growthbookrefresh', () => {
    sync(getGrowthBookInstance());
  });

  document.addEventListener('DOMContentLoaded', () => {
    sync(getGrowthBookInstance());
  });

  window.addEventListener(
    'resize',
    () => {
      sync(getGrowthBookInstance());
    },
    { passive: true }
  );
})();
